// Musical model for the metronome, kept free of Web Audio so it can be unit
// tested without an AudioContext. The engine (useMetronomeEngine) turns the
// slots described here into scheduled sound; nothing in this file makes noise.

export type Subdivision = 1 | 2 | 3 | 4 | 'swing'

/** 0 = silent, 1 = normal, 2 = accent. Used for the per-beat accent pattern. */
export type AccentLevel = 0 | 1 | 2

/** What a single scheduled tick sounds like. */
export type TickLevel = 'accent' | 'beat' | 'sub'

export interface ParsedMeter {
  numerator: number
  denominator: number
}

export const MIN_BPM = 20
export const MAX_BPM = 300
export const DEFAULT_BPM = 120
export const DEFAULT_TIME_SIGNATURE = '4/4'

/** Denominators we offer in the stepper. Anything else still parses fine. */
export const DENOMINATORS = [2, 4, 8, 16] as const

export const SUBDIVISIONS: Subdivision[] = [1, 2, 3, 4, 'swing']

export function clampBpm(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_BPM
  return Math.min(Math.max(Math.round(value), MIN_BPM), MAX_BPM)
}

/**
 * Parse "6/8" into its parts. Falls back to 4/4 rather than producing NaN —
 * the old engine divided by a zero numerator and silently produced an infinite
 * beat duration, which made the metronome look alive but never tick.
 */
export function parseTimeSignature(value: string | null | undefined): ParsedMeter {
  const [rawNum, rawDen] = String(value ?? '').split('/')
  const numerator = Number(rawNum)
  const denominator = Number(rawDen)

  // Note the `>= 1` rather than a clamp: Number('') is 0, so an absent or
  // empty signature would otherwise be "corrected" to a 1-beat bar instead of
  // falling back to 4/4.
  const usableNumerator = Number.isFinite(numerator) && numerator >= 1

  return {
    numerator: usableNumerator ? Math.min(Math.trunc(numerator), 16) : 4,
    denominator: (DENOMINATORS as readonly number[]).includes(denominator) ? denominator : 4,
  }
}

export function formatTimeSignature(numerator: number, denominator: number): string {
  return `${numerator}/${denominator}`
}

/**
 * True for meters that are conventionally felt in dotted-quarter pulses:
 * 6/8, 9/8, 12/8. 3/8 is excluded — it's normally counted in three.
 */
export function isCompoundCapable(value: string): boolean {
  const { numerator, denominator } = parseTimeSignature(value)
  return denominator === 8 && numerator > 3 && numerator % 3 === 0
}

/**
 * How many pulses the bar has — i.e. how many dots to draw and how many
 * entries the accent pattern needs.
 *
 * The previous engine only ever read the numerator, so 6/8 came out as six
 * equal clicks. With `compound` on it is two dotted-quarter pulses of three,
 * which is how 6/8 is actually felt. The flag stays user-settable because
 * slow 6/8 is sometimes genuinely counted in six.
 */
export function beatsPerBar(timeSignature: string, compound = false): number {
  const { numerator } = parseTimeSignature(timeSignature)
  if (compound && isCompoundCapable(timeSignature)) {
    return numerator / 3
  }
  return numerator
}

/** How many ticks fall inside one pulse. */
export function subdivisionCount(subdivision: Subdivision): number {
  return subdivision === 'swing' ? 2 : subdivision
}

/**
 * Where each tick sits inside a pulse, as a fraction of the beat.
 * Swing pushes the offbeat to 2/3 for a triplet feel.
 */
export function subdivisionOffsets(subdivision: Subdivision): number[] {
  switch (subdivision) {
    case 1:
      return [0]
    case 2:
      return [0, 0.5]
    case 3:
      return [0, 1 / 3, 2 / 3]
    case 4:
      return [0, 0.25, 0.5, 0.75]
    case 'swing':
      return [0, 2 / 3]
    default:
      return [0]
  }
}

/** Compound meters carry three inherent divisions, so that's the sensible default. */
export function defaultSubdivision(timeSignature: string, compound: boolean): Subdivision {
  return compound && isCompoundCapable(timeSignature) ? 3 : 1
}

/** A fresh pattern: accent the downbeat, everything else normal. */
export function defaultAccents(length: number): AccentLevel[] {
  return Array.from({ length }, (_, i) => (i === 0 ? 2 : 1))
}

/**
 * Fit an existing pattern to a new bar length, keeping the accents the user
 * already set instead of wiping them every time the meter changes.
 */
export function resizeAccents(pattern: AccentLevel[] | undefined, length: number): AccentLevel[] {
  if (length < 1) return []
  const source = Array.isArray(pattern) ? pattern : []
  const next = defaultAccents(length)

  for (let i = 0; i < length && i < source.length; i++) {
    const level = source[i]
    if (level === 0 || level === 1 || level === 2) {
      next[i] = level
    }
  }
  return next
}

/**
 * i18n key fragments. Kept here so numeric values never end up as translation
 * path segments (`subdivisions.4` is a fragile lookup; `subdivisions.sixteenth`
 * is not).
 */
export function subdivisionKey(subdivision: Subdivision): string {
  switch (subdivision) {
    case 2:
      return 'eighth'
    case 3:
      return 'triplet'
    case 4:
      return 'sixteenth'
    case 'swing':
      return 'swing'
    default:
      return 'quarter'
  }
}

export function accentKey(level: AccentLevel): string {
  if (level === 2) return 'accent'
  if (level === 0) return 'silent'
  return 'normal'
}

/** Cycle order used when tapping a beat dot: accent → normal → silent → accent. */
export function cycleAccent(level: AccentLevel): AccentLevel {
  if (level === 2) return 1
  if (level === 1) return 0
  return 2
}

/**
 * The sound tier for one tick. Subdivision ticks are always the quiet tier,
 * and a silenced beat silences its subdivisions with it.
 */
export function tickLevel(accent: AccentLevel, slot: number): TickLevel | null {
  if (accent === 0) return null
  if (slot > 0) return 'sub'
  return accent === 2 ? 'accent' : 'beat'
}
